import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios-cad-nx';
import { useToast } from '@cad-arc';
import isElectron from 'is-electron';
import { ApiResult } from '@mark43/cad-api';
import type { ExtraConfig, TypeBoundAxiosInstance } from '@m43/shared';
import { logout } from '~/session';

/** The toast callable returned by arc's `useToast()`. */
export type Toast = ReturnType<typeof useToast>;

/**
 * A 401 on web means the session is gone, so bounce to login. Electron manages its
 * own session lifecycle and handles this elsewhere.
 */
export function attachUnauthorizedHandler(instance: TypeBoundAxiosInstance): void {
    instance.interceptors.response.use(
        (response) => response,
        (err: AxiosError) => {
            if (err.response?.status === 401 && !isElectron()) {
                logout();
                return;
            }
            return Promise.reject(err);
        }
    );
}

/**
 * Surfaces partial-success (207) and bad-request (400) messages as toasts, unless the
 * caller opted out with `shouldFailSilently`.
 *
 * Attach this to whichever instance is currently in use — attaching it only to the
 * initial instance means a later client swap loses the handler.
 */
export function attachErrorToastHandler(instance: TypeBoundAxiosInstance, toast: Toast): number {
    return instance.interceptors.response.use(
        (response: AxiosResponse<ApiResult>) => {
            if (!response) {
                return response;
            }
            const config: AxiosRequestConfig & ExtraConfig = response.config;
            if (response.status === 207 && response.data.error && !config.shouldFailSilently) {
                toast({ description: response.data.error, status: 'default' });
            }
            return response;
        },
        (error: AxiosError<ApiResult>) => {
            const config = error.config as AxiosRequestConfig & ExtraConfig;
            if (error.response?.status === 400 && !config.shouldFailSilently) {
                toast({ description: error.response.data.error, status: 'default' });
            }
            return Promise.reject(error);
        }
    );
}

export function detachResponseInterceptor(
    instance: TypeBoundAxiosInstance,
    id: number | undefined
): void {
    if (typeof id === 'number') {
        instance.interceptors.response.eject(id);
    }
}
